import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Input,
  InputNumber,
  Row,
  Select,
  Table,
  Tag,
  Upload,
  message,
} from 'antd';
import axios from '../../util/Api';
import './style.css';

const { Option } = Select;

const POINT_OF_INTEREST_RESOURCE = 'PointOfInterest';
const DEFAULT_NAME_FIELDS = 'name,label,stand_name,standName,stand_id,standId,id,title';
const DEFAULT_STAND_FIELDS = 'stand_id,standId,stand,stand_no,standNo,id,label';

const CATEGORY_OPTIONS = [
  { value: 'other', label: 'Other (default)' },
  { value: 'runway', label: 'Runway' },
  { value: 'taxiway', label: 'Taxiway' },
  { value: 'apron', label: 'Apron' },
  { value: 'terminal', label: 'Terminal' },
  { value: 'hangar', label: 'Hangar' },
  { value: 'parking_area', label: 'Parking Area' },
  { value: 'maintenance_area', label: 'Maintenance Area' },
  { value: 'road', label: 'Road' },
  { value: 'building', label: 'Building' },
  { value: 'bay_area', label: 'Bay Area' },
  { value: 'gate', label: 'Gate' },
  { value: 'restricted_zone', label: 'Restricted Zone' },
];
const POINT_SHAPE_OPTIONS = ['polygon', 'circle', 'rectangle'];

function flattenRoleAccess(roles = []) {
  return (roles || []).reduce((acc, role) => acc.concat((role && role.access) || []), []);
}

function getResourcePermissions(roles = [], resourceName = '') {
  const accessEntries = flattenRoleAccess(roles);
  const permissionSet = new Set();
  accessEntries.forEach((entry) => {
    if (!entry || entry.name !== resourceName || !Array.isArray(entry.permissions)) return;
    entry.permissions.forEach((permission) => permissionSet.add(String(permission)));
  });
  return Array.from(permissionSet);
}

function getUserOrganisationMeta(user) {
  const organisation = user && user.organisation;
  if (!organisation) return { id: null, name: '' };
  if (typeof organisation === 'string') return { id: String(organisation), name: '' };
  const id = organisation._id || organisation.id || null;
  const name = organisation.name || organisation.label || '';
  return { id: id ? String(id) : null, name: name ? String(name) : '' };
}

function getUserFromLocalStorage() {
  try {
    const user = window.localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch (e) {
    return null;
  }
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Unable to read selected file'));
    reader.readAsText(file);
  });
}

function statusTag(status) {
  if (status === 'valid') return <Tag color="green">Valid</Tag>;
  if (status === 'partial_duplicate') return <Tag color="gold">Partial Duplicate</Tag>;
  if (status === 'duplicate') return <Tag color="orange">Duplicate</Tag>;
  return <Tag color="red">Invalid</Tag>;
}

function isSelectablePreviewRow(row) {
  return !!(row && row.status === 'valid');
}

export default function GeoFenceImport({ authUser }) {
  const resolvedUser = authUser || getUserFromLocalStorage();
  const isSuperAdmin = !!(resolvedUser && resolvedUser.superAdmin);
  const loggedUserOrganisation = useMemo(
    () => getUserOrganisationMeta(resolvedUser),
    [resolvedUser],
  );
  const pointOfInterestPermissions = useMemo(() => {
    if (isSuperAdmin) return ['c', 'r', 'u', 'd'];
    return getResourcePermissions((resolvedUser && resolvedUser.roles) || [], POINT_OF_INTEREST_RESOURCE);
  }, [isSuperAdmin, resolvedUser]);
  const canCreatePointOfInterest = isSuperAdmin || pointOfInterestPermissions.includes('c');

  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisations, setSelectedOrganisations] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [parsedGeoJson, setParsedGeoJson] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmingImport, setConfirmingImport] = useState(false);
  const [previewResponse, setPreviewResponse] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [pointToPolygon, setPointToPolygon] = useState(true);
  const [pointShapeType, setPointShapeType] = useState('polygon');
  const [pointRadiusMeters, setPointRadiusMeters] = useState(15);
  const [pointPolygonVertices, setPointPolygonVertices] = useState(25);
  const [nameFields, setNameFields] = useState(DEFAULT_NAME_FIELDS);
  const [standIdFields, setStandIdFields] = useState(DEFAULT_STAND_FIELDS);
  const [selectedSourceIndexes, setSelectedSourceIndexes] = useState([]);
  const [previewSearchQuery, setPreviewSearchQuery] = useState('');
  const [recordOverrides, setRecordOverrides] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [importCategory, setImportCategory] = useState('other');

  useEffect(() => {
    if (!canCreatePointOfInterest) return;
    if (isSuperAdmin) {
      axios.get('/organisation/distinct')
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : [];
          setOrganisations(rows);
        })
        .catch(() => {
          message.error('Unable to load organisations');
        });
      return;
    }

    if (loggedUserOrganisation.id) {
      setOrganisations([{ _id: loggedUserOrganisation.id, name: loggedUserOrganisation.name || 'Current Organisation' }]);
    }
  }, [canCreatePointOfInterest, isSuperAdmin, loggedUserOrganisation]);

  const handleGeoJsonFileSelect = async (file) => {
    if (!(selectedOrganisations || []).length) {
      message.warning('Select at least one organisation before selecting a GeoJSON file');
      return false;
    }
    try {
      const text = await readFileAsText(file);
      const payload = JSON.parse(String(text || ''));
      setParsedGeoJson(payload);
      setSelectedFileName(file && file.name ? String(file.name) : 'uploaded.geojson');
      setPreviewResponse(null);
      setImportResult(null);
      setSelectedSourceIndexes([]);
      setRecordOverrides({});
      setEditingCell(null);
      setEditingValue('');
      message.success('GeoJSON file loaded');
    } catch (error) {
      const errorMessage = error && error.message ? error.message : 'Invalid GeoJSON file';
      setParsedGeoJson(null);
      setSelectedFileName('');
      setPreviewResponse(null);
      setImportResult(null);
      setSelectedSourceIndexes([]);
      setRecordOverrides({});
      setEditingCell(null);
      setEditingValue('');
      message.error(errorMessage);
    }
    return false;
  };

  const buildImportPayload = (extra = {}) => {
    const payload = {
      organisations: selectedOrganisations,
      geoJson: parsedGeoJson,
      pointToPolygon: !!pointToPolygon,
      pointShapeType,
      pointRadiusMeters: Number(pointRadiusMeters),
      pointPolygonVertices: Number(pointPolygonVertices),
      nameFields: String(nameFields || '')
        .split(',')
        .map((field) => String(field || '').trim())
        .filter((field) => !!field),
      standIdFields: String(standIdFields || '')
        .split(',')
        .map((field) => String(field || '').trim())
        .filter((field) => !!field),
      recordOverrides,
      category: importCategory || 'other',
      layer: null,
    };
    return { ...payload, ...extra };
  };

  const generatePreview = async ({ silent = false, keepSelection = false, overrides = recordOverrides } = {}) => {
    if (!(selectedOrganisations || []).length) {
      if (!silent) message.warning('Select at least one organisation');
      return;
    }
    if (!parsedGeoJson) {
      if (!silent) message.warning('Upload a GeoJSON file first');
      return;
    }
    try {
      setLoadingPreview(true);
      const response = await axios.post('/geo-fence/import/preview', buildImportPayload({
        recordOverrides: overrides,
      }));
      const data = response && response.data ? response.data : null;
      setPreviewResponse(data);
      setSelectedSourceIndexes((prev) => {
        if (!keepSelection) return [];
        const selectableSourceIndexes = new Set(
          ((data && data.preview) || [])
            .filter((row) => isSelectablePreviewRow(row))
            .map((row) => Number(row.sourceIndex))
            .filter((value) => Number.isFinite(value) && value >= 0),
        );
        return (prev || []).filter((value) => selectableSourceIndexes.has(Number(value)));
      });
      if (!silent) message.success('Preview generated');
    } catch (error) {
      const responseMessage = error && error.response && error.response.data && error.response.data.message;
      const msg = Array.isArray(responseMessage) ? responseMessage[0] : responseMessage;
      if (!silent) message.error(msg || 'Unable to preview GeoJSON import');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handlePreview = async () => {
    await generatePreview({ silent: false, keepSelection: false });
  };

  const startInlineEdit = (row, field) => {
    if (!row) return;
    const sourceIndex = Number(row.sourceIndex);
    if (!Number.isFinite(sourceIndex) || sourceIndex < 0) return;
    setEditingCell({ sourceIndex, field });
    setEditingValue(String(row[field] || ''));
  };

  const commitInlineEdit = async () => {
    if (!editingCell) return;
    const sourceIndex = Number(editingCell.sourceIndex);
    const field = editingCell.field;
    if (!Number.isFinite(sourceIndex) || sourceIndex < 0 || !field) {
      setEditingCell(null);
      setEditingValue('');
      return;
    }

    const normalizedValue = String(editingValue || '').trim();
    const nextOverrides = {
      ...(recordOverrides || {}),
      [sourceIndex]: {
        ...((recordOverrides && recordOverrides[sourceIndex]) || {}),
        [field]: normalizedValue,
      },
    };

    setRecordOverrides(nextOverrides);
    setPreviewResponse((prev) => {
      if (!prev || !Array.isArray(prev.preview)) return prev;
      return {
        ...prev,
        preview: prev.preview.map((row) => (
          Number(row && row.sourceIndex) === sourceIndex
            ? { ...row, [field]: normalizedValue }
            : row
        )),
      };
    });
    setEditingCell(null);
    setEditingValue('');
    await generatePreview({ silent: true, keepSelection: true, overrides: nextOverrides });
  };

  const cancelInlineEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const handleConfirmImport = async () => {
    if (!previewResponse || !previewResponse.summary) {
      message.warning('Generate preview before import');
      return;
    }
    if (!(previewResponse.summary.valid || 0)) {
      message.warning('No valid records available to import');
      return;
    }
    if (!selectedSourceIndexes.length) {
      message.warning('Select at least one valid record from preview');
      return;
    }
    try {
      setConfirmingImport(true);
      const response = await axios.post('/geo-fence/import/confirm', buildImportPayload({
        selectedSourceIndexes,
      }));
      setImportResult(response && response.data ? response.data : null);
      message.success('GeoJSON import completed');
      handlePreview();
    } catch (error) {
      const responseMessage = error && error.response && error.response.data && error.response.data.message;
      const msg = Array.isArray(responseMessage) ? responseMessage[0] : responseMessage;
      message.error(msg || 'Unable to complete GeoJSON import');
    } finally {
      setConfirmingImport(false);
    }
  };

  const previewRows = (previewResponse && Array.isArray(previewResponse.preview))
    ? previewResponse.preview.map((row, index) => ({
      ...row,
      key: String((row && row.sourceIndex !== undefined) ? row.sourceIndex : index),
    }))
    : [];
  const filteredPreviewRows = useMemo(() => {
    const query = String(previewSearchQuery || '').trim().toLowerCase();
    if (!query) return previewRows;
    return (previewRows || []).filter((row) => {
      const name = String((row && row.name) || '').toLowerCase();
      const standId = String((row && row.standId) || '').toLowerCase();
      return name.includes(query) || standId.includes(query);
    });
  }, [previewRows, previewSearchQuery]);
  const hideStandIdColumn = !!(previewResponse && previewResponse.previewDisplay && previewResponse.previewDisplay.hideStandIdColumn);

  const baseColumns = [
    {
      title: '#',
      dataIndex: 'sourceIndex',
      key: 'sourceIndex',
      width: 70,
      render: (value) => Number(value) + 1,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 210,
      render: (value, row) => {
        const isEditing = !!(editingCell
          && Number(editingCell.sourceIndex) === Number(row && row.sourceIndex)
          && editingCell.field === 'name');
        if (isEditing) {
          return (
            <Input
              autoFocus
              size="small"
              value={editingValue}
              onChange={(event) => setEditingValue(event.target.value)}
              onPressEnter={commitInlineEdit}
              onBlur={commitInlineEdit}
              onKeyDown={(event) => {
                if (event.key === 'Escape') cancelInlineEdit();
              }}
            />
          );
        }
        return (
          <span className="geo-fence-import-editable-cell" onClick={() => startInlineEdit(row, 'name')}>
            {value || '-'}
          </span>
        );
      },
    },
  ];
  const standIdColumn = {
    title: 'Stand ID',
    dataIndex: 'standId',
    key: 'standId',
    width: 120,
    render: (value, row) => {
      const isEditing = !!(editingCell
        && Number(editingCell.sourceIndex) === Number(row && row.sourceIndex)
        && editingCell.field === 'standId');
      if (isEditing) {
        return (
          <Input
            autoFocus
            size="small"
            value={editingValue}
            onChange={(event) => setEditingValue(event.target.value)}
            onPressEnter={commitInlineEdit}
            onBlur={commitInlineEdit}
            onKeyDown={(event) => {
              if (event.key === 'Escape') cancelInlineEdit();
            }}
          />
        );
      }
      return (
        <span className="geo-fence-import-editable-cell" onClick={() => startInlineEdit(row, 'standId')}>
          {value || '-'}
        </span>
      );
    },
  };
  const trailingColumns = [
    {
      title: 'Geometry',
      key: 'geometry',
      width: 170,
      render: (_, row) => `${row.sourceGeometryType || '-'} -> ${row.geometryType || '-'}`,
    },
    {
      title: 'Target Shape',
      dataIndex: 'shapeType',
      key: 'shapeType',
      width: 130,
      render: (value) => value || '-',
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, row) => (
        <span>
          {statusTag(row.status)}
          {row.convertedFromPoint ? <Tag color="blue">Point Converted</Tag> : null}
        </span>
      ),
    },
    {
      title: 'Remark',
      dataIndex: 'reason',
      key: 'reason',
      render: (value) => value || '-',
    },
  ];
  const columns = hideStandIdColumn
    ? baseColumns.concat(trailingColumns)
    : baseColumns.concat([standIdColumn], trailingColumns);

  const rowSelection = {
    selectedRowKeys: selectedSourceIndexes.map((value) => String(value)),
    onChange: (nextSelectedRowKeys) => {
      const nextIndexes = (nextSelectedRowKeys || [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value >= 0);
      setSelectedSourceIndexes(nextIndexes);
    },
    getCheckboxProps: (record) => ({
      disabled: !isSelectablePreviewRow(record),
    }),
  };

  if (!canCreatePointOfInterest) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Unauthorized"
          description="Create permission for Point of Interest is required."
        />
      </Card>
    );
  }

  return (
    <div className="geo-fence-import-page">
      <Row gutter={12}>
        <Col xs={24} lg={9} xl={8}>
          <Card title="GeoJSON Import Configuration" className="geo-fence-import-panel">
            <div className="geo-fence-import-field">
              <label>Organisation</label>
              <Select
                mode={isSuperAdmin ? 'multiple' : undefined}
                value={selectedOrganisations}
                onChange={(value) => setSelectedOrganisations(Array.isArray(value) ? value : (value ? [value] : []))}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
                placeholder="Select one or more organisations"
                maxTagCount={2}
              >
                {(organisations || []).map((item) => (
                  <Option key={String(item._id)} value={String(item._id)}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="geo-fence-import-field">
              <label>GeoJSON File</label>
              <Upload
                accept=".geojson,.json,application/geo+json,application/json"
                beforeUpload={handleGeoJsonFileSelect}
                showUploadList={false}
                disabled={!(selectedOrganisations || []).length}
              >
                <Button icon="upload" block disabled={!(selectedOrganisations || []).length}>Select GeoJSON File</Button>
              </Upload>
              <small className="geo-fence-import-help-text">
                {selectedFileName
                  ? `Selected: ${selectedFileName}`
                  : ((selectedOrganisations || []).length
                    ? 'No file selected yet'
                    : 'Select organisation first, then choose a GeoJSON file')}
              </small>
            </div>

            <Divider />

            <div className="geo-fence-import-field">
              <Checkbox checked={pointToPolygon} onChange={(event) => setPointToPolygon(!!(event && event.target && event.target.checked))}>
                Convert Point features to drawable shape
              </Checkbox>
            </div>

            {pointToPolygon && (
              <Row gutter={8}>
                <Col span={12}>
                  <div className="geo-fence-import-field">
                    <label>Point Shape Type</label>
                    <Select
                      value={pointShapeType}
                      onChange={(value) => setPointShapeType(value)}
                      style={{ width: '100%' }}
                    >
                      {POINT_SHAPE_OPTIONS.map((shape) => (
                        <Option key={shape} value={shape}>{shape}</Option>
                      ))}
                    </Select>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="geo-fence-import-field">
                    <label>Point Radius (m)</label>
                    <InputNumber
                      min={1}
                      max={5000}
                      value={pointRadiusMeters}
                      onChange={(value) => setPointRadiusMeters(value || 15)}
                      style={{ width: '100%' }}
                    />
                  </div>
                </Col>
                {(pointShapeType === 'polygon' || pointShapeType === 'circle') && (
                  <Col span={12}>
                    <div className="geo-fence-import-field">
                      <label>Polygon Vertices</label>
                      <InputNumber
                        min={8}
                        max={180}
                        value={pointPolygonVertices}
                        onChange={(value) => setPointPolygonVertices(value || 25)}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Col>
                )}
                {pointShapeType === 'rectangle' && (
                  <Col span={24}>
                    <div className="geo-fence-import-field">
                      <small className="geo-fence-import-help-text">Rectangle is generated as square around point using the selected radius.</small>
                    </div>
                  </Col>
                )}
              </Row>
            )}

            <div className="geo-fence-import-field">
              <label>Name Fields Priority</label>
              <Input
                value={nameFields}
                onChange={(event) => setNameFields(event.target.value)}
                placeholder="name,label,stand_id"
              />
            </div>

            <div className="geo-fence-import-field">
              <label>Stand ID Fields Priority</label>
              <Input
                value={standIdFields}
                onChange={(event) => setStandIdFields(event.target.value)}
                placeholder="stand_id,standId,id"
              />
            </div>

            <Divider />

            <div className="geo-fence-import-field">
              <label>Category</label>
              <Select
                value={importCategory}
                onChange={(value) => setImportCategory(value)}
                style={{ width: '100%' }}
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
              <small className="geo-fence-import-help-text">Category applied to all imported features.</small>
            </div>

            <div className="geo-fence-import-actions">
              <Button type="primary" onClick={handlePreview} loading={loadingPreview}>Generate Preview</Button>
              <Button
                type="danger"
                onClick={handleConfirmImport}
                loading={confirmingImport}
                disabled={!previewResponse || !(previewResponse.summary && (previewResponse.summary.valid || 0)) || !selectedSourceIndexes.length}
              >
                Confirm Import
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={15} xl={16}>
          <Card title="Preview Records" className="geo-fence-import-preview-panel">
            {previewResponse && previewResponse.summary ? (
              <div className="geo-fence-import-summary-row">
                <Tag color="blue">Total: {previewResponse.summary.totalFeatures || 0}</Tag>
                <Tag color="green">Valid: {previewResponse.summary.valid || 0}</Tag>
                <Tag color="gold">Partial Duplicate: {previewResponse.summary.partialDuplicates || 0}</Tag>
                <Tag color="orange">Duplicate: {previewResponse.summary.duplicates || 0}</Tag>
                <Tag color="red">Invalid: {previewResponse.summary.invalid || 0}</Tag>
                <Tag color="purple">Point Converted: {previewResponse.summary.pointConvertedToPolygon || 0}</Tag>
                <Tag color="geekblue">Selected: {selectedSourceIndexes.length}</Tag>
              </div>
            ) : (
              <Alert
                showIcon
                type="info"
                message="Upload file and click Generate Preview to inspect records before saving."
              />
            )}

            <div className="geo-fence-import-field">
              <Input
                value={previewSearchQuery}
                onChange={(event) => setPreviewSearchQuery(event.target.value)}
                placeholder="Search preview by stand_id or name"
              />
              <small className="geo-fence-import-help-text">
                Click Name or Stand ID to edit inline. Existing records for selected organisation(s) remain non-selectable.
                {hideStandIdColumn ? ' Stand ID column is hidden because Name is derived from stand_id for all preview rows.' : ''}
              </small>
            </div>

            <div className="geo-fence-import-table-wrap">
              <Table
                columns={columns}
                dataSource={filteredPreviewRows}
                rowSelection={rowSelection}
                pagination={{ pageSize: 20, showSizeChanger: true }}
                size="small"
                scroll={{ x: 900 }}
              />
            </div>

            {importResult && importResult.summary ? (
              <Alert
                type={importResult.summary.failed ? 'warning' : 'success'}
                showIcon
                message={`Import result: Created ${importResult.summary.created || 0}, Failed ${importResult.summary.failed || 0}, Skipped ${importResult.summary.skipped || 0}`}
                description={importResult.summary.failed ? 'Some records failed. Check duplicate/invalid records in preview and retry.' : 'All valid records imported successfully.'}
              />
            ) : null}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
